import { useTranslation } from "react-i18next";
import {
  CLAIM_SEVERITIES,
  ISSUE_CATEGORIES,
  ISSUE_TYPES,
  MONETARY_CLAIM_TYPES,
  allowedClaimTypes,
  issueDefinition,
  resolveSeverity,
  type ClaimSeverity,
  type ClaimSide,
  type ClaimType,
} from "@hisense/shared";
import { FieldLabel, Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";

/** Một dòng yêu cầu đang soạn: số tiền là CHUỖI người gõ — chuẩn hoá ở `normalizeClaims`, không parseFloat. */
export interface ClaimDraftLine {
  type: ClaimType;
  amount: string;
}

export interface IssueClaimValue {
  issueCode: string;
  severity: ClaimSeverity | null;
  claims: ClaimDraftLine[];
  claimCurrency: string;
}

/**
 * Loại lỗi + mức độ + yêu cầu — MỘT bộ ô cho cả khiếu nại của khách và khiếu nại
 * nhà cung cấp, để hai hồ sơ luôn nói cùng một ngôn ngữ (Louis, 17/09/2026: hai hồ
 * sơ phải nhất quán). `side` chỉ quyết định hình thức nào được hiện.
 *
 * Đổi loại lỗi thì bỏ các yêu cầu không còn áp dụng thay vì giữ lại âm thầm: một
 * yêu cầu lạc loại sẽ bị máy chủ từ chối lúc lưu, và người dùng không hiểu vì sao.
 */
export function IssueClaimFields({
  side,
  value,
  onChange,
  disabled,
  idPrefix,
}: {
  side: ClaimSide;
  value: IssueClaimValue;
  onChange: (next: IssueClaimValue) => void;
  disabled?: boolean;
  idPrefix: string;
}) {
  const { t } = useTranslation();
  const def = issueDefinition(value.issueCode);
  const allowed = value.issueCode ? allowedClaimTypes(value.issueCode, side) : [];
  const severity = value.issueCode ? resolveSeverity(value.issueCode, value.severity) : null;
  const hasMoney = value.claims.some((c) => MONETARY_CLAIM_TYPES.has(c.type) && c.amount.trim() !== "");

  const setIssue = (code: string) => {
    const keep = new Set(code ? allowedClaimTypes(code, side) : []);
    onChange({
      ...value,
      issueCode: code,
      severity: issueDefinition(code)?.severity ?? null,
      claims: value.claims.filter((c) => keep.has(c.type)),
    });
  };
  const toggle = (type: ClaimType, on: boolean) => {
    if (on && type === "RECORD_ONLY") return onChange({ ...value, claims: [{ type, amount: "" }] });
    const rest = value.claims.filter((c) => c.type !== type && (!on || c.type !== "RECORD_ONLY"));
    onChange({ ...value, claims: on ? [...rest, { type, amount: "" }] : rest });
  };
  const setAmount = (type: ClaimType, amount: string) =>
    onChange({ ...value, claims: value.claims.map((c) => (c.type === type ? { ...c, amount } : c)) });

  return (
    <>
      <div>
        <FieldLabel htmlFor={`${idPrefix}-issue`}>{t("claims.issue")}</FieldLabel>
        <Select
          id={`${idPrefix}-issue`}
          containerClassName="w-full min-h-11"
          value={value.issueCode}
          disabled={disabled}
          onChange={(e) => setIssue(e.target.value)}
        >
          <option value="">{t("claims.chooseIssue")}</option>
          {ISSUE_CATEGORIES.map((cat) => (
            <optgroup key={cat} label={t(`claims.categories.${cat}`)}>
              {ISSUE_TYPES.filter((i) => i.category === cat).map((i) => (
                <option key={i.code} value={i.code}>
                  {t(`claims.issues.${i.code}.label`)}
                </option>
              ))}
            </optgroup>
          ))}
        </Select>
        {def && <p className="mt-1 text-xs text-ink-soft">{t(`claims.issues.${def.code}.desc`)}</p>}
      </div>
      <div>
        <FieldLabel htmlFor={`${idPrefix}-severity`}>{t("claims.severity")}</FieldLabel>
        {def && def.severity === null ? (
          <Select
            id={`${idPrefix}-severity`}
            containerClassName="w-full min-h-11"
            value={value.severity ?? ""}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, severity: (e.target.value || null) as ClaimSeverity | null })}
          >
            <option value="">—</option>
            {CLAIM_SEVERITIES.filter((s) => s !== "Critical").map((s) => (
              <option key={s} value={s}>
                {t(`claims.severities.${s}`)}
              </option>
            ))}
          </Select>
        ) : (
          <p id={`${idPrefix}-severity`} className="flex min-h-11 items-center gap-2 text-sm">
            {severity ? t(`claims.severities.${severity}`) : "—"}
            {severity && <span className="text-xs text-ink-soft">· {t("claims.severityFromIssue")}</span>}
          </p>
        )}
      </div>
      {def && (
        <fieldset className="md:col-span-2" disabled={disabled}>
          <legend className="text-sm font-medium">{t(side === "customer" ? "claims.claims" : "claims.claimsSupplier")}</legend>
          <p className="mb-2 text-xs text-ink-soft">{t("claims.claimsHint")}</p>
          <ul className="grid gap-2 md:grid-cols-2">
            {allowed.map((type) => {
              const line = value.claims.find((c) => c.type === type);
              const money = MONETARY_CLAIM_TYPES.has(type);
              return (
                <li key={type} className="flex min-h-11 flex-wrap items-center gap-2 rounded-sm border border-line px-3 py-2">
                  <label className="flex flex-1 items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!line} onChange={(e) => toggle(type, e.target.checked)} />
                    {t(`claims.types.${type}`)}
                  </label>
                  {line && money && (
                    <Input
                      aria-label={`${t(`claims.types.${type}`)} · ${t("claims.amount")}`}
                      className="w-32 font-mono tabular"
                      inputMode="decimal"
                      placeholder={t("claims.amount")}
                      value={line.amount}
                      onChange={(e) => setAmount(type, e.target.value)}
                    />
                  )}
                </li>
              );
            })}
          </ul>
          {hasMoney && (
            <div className="mt-3 max-w-40">
              <FieldLabel htmlFor={`${idPrefix}-currency`}>{t("claims.currency")}</FieldLabel>
              <Input
                id={`${idPrefix}-currency`}
                className="min-h-11 font-mono uppercase"
                maxLength={3}
                value={value.claimCurrency}
                onChange={(e) => onChange({ ...value, claimCurrency: e.target.value.toUpperCase().replace(/[^A-Z]/g, "") })}
              />
            </div>
          )}
        </fieldset>
      )}
    </>
  );
}
